// Copyright ©️ 2026 eVolve MEP, LLC

import { type Facility, type FacilityUpdate, type RenderableUser, useEvolvePatch } from '@evolvemep/api-ts';
import { Button, Flex, NumberInput, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AssigneeSelect } from 'components/Mantine/AssigneeSelect';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { UserInfoBadge } from 'components/Mantine/UserInfoBadge';
import type { StrongOmit, UseState, WithRequiredNotNull } from 'helpers/helper-types';
import type { FacilityActionModalState } from 'modules/Admin/Facilities/FacilityActionModals';
import { EditableDetailItem } from 'modules/Admin/Facilities/FacilityProperties/EditableDetailItem';

export type FacilityPropertiesFormProps = {
  actionFacility: UseState<FacilityActionModalState | null>;
  selectedFacility: Facility;
  onFacilityUpdated: (updatedFacility: Facility) => void;
  editing: boolean;
  setEditing: (editing: boolean) => void;
};

type FormBody = WithRequiredNotNull<StrongOmit<FacilityUpdate, 'deliveryLeadTime' | 'memberIds'>> & {
  /** Days. Empty while the input is cleared. */
  deliveryLeadTime: number | '';
  members: RenderableUser[];
};
/** What `validate` guarantees is true by the time `onSubmit` runs. */
type ValidatedFormBody = StrongOmit<FormBody, 'deliveryLeadTime'> & { deliveryLeadTime: number };

const toFormBody = (facility: Facility): FormBody => ({
  facilityName: facility.facilityName,
  deliveryLeadTime: facility.deliveryLeadTime,
  addressLine1: facility.addressLine1 ?? '',
  addressLine2: facility.addressLine2 ?? '',
  city: facility.city ?? '',
  state: facility.state ?? '',
  postalCode: facility.postalCode ?? '',
  country: facility.country ?? '',
  members: facility.members,
});

const toFacilityUpdate = ({ members, ...values }: ValidatedFormBody): FacilityUpdate => ({
  ...values,
  memberIds: members.map((u) => u.userId),
});

export const FacilityPropertiesForm = ({
  actionFacility,
  selectedFacility,
  onFacilityUpdated,
  editing,
  setEditing,
}: FacilityPropertiesFormProps) => {
  const navigate = useEvolveNavigate('Facilities');
  const { apiCall: updateCall, loading: saving } = useEvolvePatch('shop/facility/:facilityId');

  const form = useForm<FormBody>({
    validate: {
      facilityName: (n) => !n,
      deliveryLeadTime: (v) => !Number.isInteger(v),
    },
  });

  const onSubmit = (rawValues: typeof form.values) => {
    const values = rawValues as ValidatedFormBody; // safe — validate() has already run
    void updateCall(toFacilityUpdate(values), {
      url: `shop/facility/${selectedFacility.facilityId}`,
    }).then((updatedFacility) => {
      setEditing(false);
      onFacilityUpdated(updatedFacility);
      const newValues = toFormBody(updatedFacility);
      form.setValues(newValues);
      form.resetDirty(newValues);
    });
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)} style={{ height: '100%', overflowY: 'auto' }}>
      <Flex direction="column">
        <Flex direction="row" gap="sm" mb="lg" justify="flex-end">
          {editing ? (
            <>
              <Button
                variant="light"
                onClick={() => {
                  setEditing(false);
                  form.reset();
                }}
                disabled={saving}
              >
                Cancel edit
              </Button>
              <Button loading={saving} disabled={!form.isDirty()} type="submit">
                Save
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => actionFacility({ action: 'open-close', facility: selectedFacility })}
                variant="light"
              >
                {selectedFacility.isClosed ? 'Activate' : 'Archive'} facility
              </Button>
              <Button
                onClick={() => navigate(`/work-cell-setup/${selectedFacility.facilityId}`)}
                variant="light"
                leftSection={<EvolveIcon icon="WorkCells" />}
              >
                Work cell setup
              </Button>
              <Button
                variant="light"
                leftSection={<EvolveIcon icon="Edit" />}
                onClick={() => {
                  const formValues = toFormBody(selectedFacility);
                  form.setInitialValues(formValues);
                  form.setValues(formValues);
                  setEditing(true);
                }}
              >
                Edit
              </Button>
            </>
          )}
        </Flex>

        <EditableDetailItem
          name="Facility Name"
          editing={editing}
          renderValue={selectedFacility.facilityName}
          EditComponent={
            <TextInput maxLength={64} required size="xs" disabled={saving} {...form.getInputProps('facilityName')} />
          }
        />
        <EditableDetailItem
          name="Delivery Lead Time"
          renderValue={`${selectedFacility.deliveryLeadTime} day${selectedFacility.deliveryLeadTime !== 1 ? 's' : ''}`}
          editing={editing}
          EditComponent={
            <NumberInput
              required
              rightSectionWidth="auto"
              rightSection={
                <Text c="dimmed" fz="sm" mx="xs">
                  days
                </Text>
              }
              size="xs"
              allowDecimal={false}
              min={0}
              disabled={saving}
              {...form.getInputProps('deliveryLeadTime')}
            />
          }
        />
        <EditableDetailItem
          name="Facility Assignees"
          tooltip="Assigned users receive work order status updates for this facility."
          editing={editing}
          renderValue={
            selectedFacility.members.length > 0 ? (
              <Flex gap="xs">
                {selectedFacility.members.map((u) => (
                  <UserInfoBadge key={u.userId} user={u} />
                ))}
              </Flex>
            ) : (
              '--'
            )
          }
          EditComponent={<AssigneeSelect {...form.getInputProps('members')} disabled={saving} />}
        />

        <EditableDetailItem
          name="Address Line 1"
          editing={editing}
          renderValue={selectedFacility.addressLine1}
          EditComponent={
            <TextInput maxLength={64} size="xs" {...form.getInputProps('addressLine1')} disabled={saving} />
          }
        />
        <EditableDetailItem
          name="Address Line 2"
          editing={editing}
          renderValue={selectedFacility.addressLine2}
          EditComponent={
            <TextInput maxLength={64} size="xs" {...form.getInputProps('addressLine2')} disabled={saving} />
          }
        />
        <EditableDetailItem
          name="City"
          editing={editing}
          renderValue={selectedFacility.city}
          EditComponent={<TextInput maxLength={64} size="xs" {...form.getInputProps('city')} disabled={saving} />}
        />
        <EditableDetailItem
          name="State"
          editing={editing}
          renderValue={selectedFacility.state}
          EditComponent={<TextInput maxLength={64} size="xs" {...form.getInputProps('state')} disabled={saving} />}
        />
        <EditableDetailItem
          name="Zip/Postal Code"
          editing={editing}
          renderValue={selectedFacility.postalCode}
          EditComponent={<TextInput maxLength={64} size="xs" {...form.getInputProps('postalCode')} disabled={saving} />}
        />
        <EditableDetailItem
          name="Country"
          editing={editing}
          renderValue={selectedFacility.country}
          EditComponent={<TextInput maxLength={64} size="xs" {...form.getInputProps('country')} disabled />}
        />
      </Flex>
    </form>
  );
};
